import React, { useEffect, useState, useRef } from "react";
import { Check, Info } from "lucide-react";
import { twilioApi } from "../../services/api";

interface SmsOptInCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

// Module-level cache so we only fetch once across all instances
let cachedHasNumber: boolean | null = null;

const SmsOptInCheckbox: React.FC<SmsOptInCheckboxProps> = ({
  checked,
  onChange,
  disabled = false,
}) => {
  const [hasNumber, setHasNumber] = useState<boolean | null>(cachedHasNumber);
  const fetched = useRef(false);

  useEffect(() => {
    if (cachedHasNumber !== null || fetched.current) return;
    fetched.current = true;

    twilioApi
      .listNumbers()
      .then((res) => {
        const numbers = res?.data?.data || [];
        const result = numbers.length > 0;
        cachedHasNumber = result;
        setHasNumber(result);
      })
      .catch(() => {
        cachedHasNumber = false;
        setHasNumber(false);
      });
  }, []);

  // Still loading
  if (hasNumber === null) return null;

  // No Twilio number — show warning
  if (!hasNumber) {
    return (
      <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3 mt-2">
        <Info className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 leading-relaxed">
          To send SMS updates to clients, first purchase a phone number under{" "}
          <span className="font-medium">Communications &rarr; Phone Numbers</span>
        </p>
      </div>
    );
  }

  // Has Twilio number — show checkbox
  return (
    <label
      className={`flex items-center gap-2.5 mt-2 select-none ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className="relative flex items-center justify-center h-[18px] w-[18px] rounded-md border transition-colors duration-150 shrink-0 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        style={{
          backgroundColor: checked ? "var(--color-primary, #0F5132)" : "#f1f5f9",
          borderColor: checked ? "var(--color-primary, #0F5132)" : "#cbd5e1",
        }}
      >
        {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </button>
      <span className="text-sm text-slate-600 leading-tight">
        Receive updates and communications via SMS
      </span>
    </label>
  );
};

export default SmsOptInCheckbox;
